// Listenvergleich für Bausteine und Quizfragen der Standardmodule – eine Datei
// für Browser und Server.
//
// Der Browser nutzt sie für „Änderungen prüfen" (Übernahme einzelner
// Änderungen in eine Anpassung, utils/moduleUpstream.ts), der Server für
// Pflicht-Updates (api/ga.js spielt dieselben Änderungen in die Anpassungen
// der Mandanten ein). Liefen zwei Fassungen auseinander, sähe ein Mandant in
// der Vorschau etwas anderes, als ein Pflicht-Update später bei ihm einträgt.
//
// Bewusst ohne feste IDs: Bausteine und Fragen sind reine Arrays. Verglichen
// wird über den kompletten Inhalt (längste gemeinsame Teilfolge); gleiche Art im
// selben Abschnitt gilt als „geändert", identischer Inhalt an anderer Stelle als
// „verschoben".

const isObj = (v) => !!v && typeof v === "object" && !Array.isArray(v);

/** Schlüssel sortiert – dieselben Inhalte ergeben immer denselben Text. */
export function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (isObj(value)) {
    return `{${Object.keys(value).sort().map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(",")}}`;
  }
  return JSON.stringify(value === undefined ? null : value);
}

export const blockIdentity = (b) => `${b && b.type}|${String((b && b.title) || "").trim().toLowerCase()}`;
export const questionIdentity = (q) => String((q && q.question) || "").trim().toLowerCase();
export const sameBlockKind = (a, b) => !!a && !!b && a.type === b.type;
export const anyQuestion = () => true;

export function diffList(base, next, identity, sameKind) {
  const b = base.map(stableStringify);
  const n = next.map(stableStringify);
  const m = b.length;
  const k = n.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(k + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = k - 1; j >= 0; j--) {
      dp[i][j] = b[i] === n[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const segments = [];
  let cur = { dels: [], ins: [], at: 0 };
  let i = 0;
  let j = 0;
  while (i < m || j < k) {
    if (i < m && j < k && b[i] === n[j]) {
      if (cur.dels.length || cur.ins.length) segments.push(cur);
      i++;
      j++;
      cur = { dels: [], ins: [], at: j };
    } else if (j < k && (i >= m || dp[i][j + 1] >= dp[i + 1][j])) {
      cur.ins.push(j);
      j++;
    } else {
      cur.dels.push(i);
      i++;
    }
  }
  if (cur.dels.length || cur.ins.length) segments.push(cur);

  const changes = [];
  const usedDel = new Set();
  const usedIns = new Set();
  const allIns = segments.flatMap((s) => s.ins);
  for (const di of segments.flatMap((s) => s.dels)) {
    const nj = allIns.find((x) => !usedIns.has(x) && n[x] === b[di]);
    if (nj === undefined) continue;
    usedDel.add(di);
    usedIns.add(nj);
    changes.push({ id: `moved-${di}-${nj}`, kind: "moved", before: base[di], after: next[nj], newIndex: nj, order: nj });
  }
  for (const seg of segments) {
    const freeIns = seg.ins.filter((x) => !usedIns.has(x));
    for (const di of seg.dels.filter((x) => !usedDel.has(x))) {
      let pick = freeIns.findIndex((nj) => identity(next[nj]) === identity(base[di]));
      if (pick < 0) pick = freeIns.findIndex((nj) => sameKind(next[nj], base[di]));
      if (pick >= 0) {
        const nj = freeIns.splice(pick, 1)[0];
        changes.push({ id: `changed-${di}-${nj}`, kind: "changed", before: base[di], after: next[nj], newIndex: nj, order: nj });
      } else {
        changes.push({ id: `removed-${di}`, kind: "removed", before: base[di], order: seg.at - 0.5 });
      }
    }
    for (const nj of freeIns) {
      changes.push({ id: `added-${nj}`, kind: "added", after: next[nj], newIndex: nj, order: nj });
    }
  }
  return changes.sort((x, y) => x.order - y.order);
}

/**
 * Wie trifft eine Änderung auf die eigene Fassung?
 *   clean    – die eigene Fassung hat die Stelle unverändert, Übernahme passt
 *   conflict – die Stelle wurde auch selbst geändert; Übernahme ersetzt das
 *   missing  – die Stelle gibt es dort nicht mehr; Übernahme fügt neu ein
 *   done     – steht dort schon so, nichts zu tun
 */
export function classifyChange(change, mine, identity) {
  const fps = new Set(mine.map(stableStringify));
  const ids = new Set(mine.map(identity));
  const fp = (item) => stableStringify(item);
  switch (change.kind) {
    case "added":
      return fps.has(fp(change.after)) ? "done" : "clean";
    case "changed":
      if (fps.has(fp(change.after))) return "done";
      if (fps.has(fp(change.before))) return "clean";
      return change.before !== undefined && ids.has(identity(change.before)) ? "conflict" : "missing";
    case "removed":
      if (fps.has(fp(change.before))) return "clean";
      return change.before !== undefined && ids.has(identity(change.before)) ? "conflict" : "done";
    case "moved":
      return fps.has(fp(change.before)) ? "clean" : "missing";
    default:
      return "done";
  }
}

/** Ausgewählte Änderungen auf eine Liste anwenden. */
export function applyListChanges(mine, next, selected, identity) {
  const work = structuredClone(mine);
  const fp = (item) => stableStringify(item);
  const locate = (item) => {
    const f = fp(item);
    let idx = work.findIndex((x) => fp(x) === f);
    if (idx < 0) {
      const id = identity(item);
      idx = work.findIndex((x) => identity(x) === id);
    }
    return idx;
  };
  const inserts = [];
  for (const c of selected) {
    if (c.kind === "changed" && c.before !== undefined && c.after !== undefined) {
      const idx = locate(c.before);
      if (idx >= 0) work[idx] = structuredClone(c.after);
      else inserts.push({ item: c.after, newIndex: c.newIndex ?? work.length });
    } else if (c.kind === "removed" && c.before !== undefined) {
      const idx = locate(c.before);
      if (idx >= 0) work.splice(idx, 1);
    } else if (c.kind === "moved" && c.before !== undefined && c.after !== undefined) {
      const f = fp(c.before);
      const idx = work.findIndex((x) => fp(x) === f);
      if (idx >= 0) work.splice(idx, 1);
      inserts.push({ item: c.after, newIndex: c.newIndex ?? work.length });
    } else if (c.kind === "added" && c.after !== undefined) {
      const f = fp(c.after);
      if (!work.some((x) => fp(x) === f)) inserts.push({ item: c.after, newIndex: c.newIndex ?? work.length });
    }
  }
  // Einfügen hinter dem nächsten Vorgänger aus der neuen Liste, den es dort gibt –
  // erst am Inhalt, dann an der Identität gesucht: ein selbst geänderter
  // Vorgänger ist inhaltlich anders, steht aber an derselben Stelle.
  inserts.sort((a, b) => a.newIndex - b.newIndex);
  for (const ins of inserts) {
    let at = -1;
    for (let k = ins.newIndex - 1; k >= 0; k--) {
      const f = fp(next[k]);
      let idx = work.findIndex((x) => fp(x) === f);
      if (idx < 0) {
        const id = identity(next[k]);
        idx = work.findIndex((x) => identity(x) === id);
      }
      if (idx >= 0) { at = idx + 1; break; }
    }
    if (at < 0) at = ins.newIndex === 0 ? 0 : Math.min(ins.newIndex, work.length);
    work.splice(at, 0, structuredClone(ins.item));
  }
  return work;
}
